import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  Rubro,
  RubroProductDetail,
  RubrosPagingRes,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

export enum RubroQueryKeyEnum {
  RUBROS = 'rubros',
  RUBRO = 'rubro',
}

///* tanStack query
export const useFetchRubros = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetRubrosParams> = {}) => {
  return useQuery({
    queryKey: [RubroQueryKeyEnum.RUBROS, ...Object.values(params ?? {})],
    queryFn: () => getRubros(params),
    enabled: enabled,
  });
};

export const useGetRubro = (id: string) => {
  return useQuery({
    queryKey: [RubroQueryKeyEnum.RUBRO, id],
    queryFn: () => getRubro(id),
    retry: false,
  });
};

export const useCreateRubro = <T>({
  navigate,
  enableRedirect = true,
  returnUrl,
  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,
  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (params: CreateRubroParams<T>) => createRubro(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [RubroQueryKeyEnum.RUBROS],
      });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Rubro creado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));
      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateRubro = <T>({
  navigate,
  enableRedirect = true,
  returnUrl,
  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,
  enableSuccessAlert = true,
  customSuccessMessage,
  customSuccessCallback,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (params: UpdateRubroParams<T>) => updateRubro(params),
    onSuccess: res => {
      queryClient.invalidateQueries({
        queryKey: [RubroQueryKeyEnum.RUBROS],
      });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Rubro actualizado correctamente',
          AlertSeverityEnum.success
        );

      customSuccessCallback && customSuccessCallback(res?.data);
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));
      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useDeleteRubro = () => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: deleteRubro,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [RubroQueryKeyEnum.RUBROS],
      });
      setAlert(
        true,
        'Rubro eliminado correctamente',
        AlertSeverityEnum.success
      );
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
    onError: error => {
      handleAxiosError(error);
    },
  });
};

///* axios
export type GetRubrosParams = Partial<Rubro> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;

  order_by?: keyof Rubro;
};

export type CreateRubroParamsForm = Omit<Rubro, 'id_rubro'>;
export type CreateRubroParams<T> = T;
export type UpdateRubroParams<T> = {
  id: string;
  data: T;
};

export const getRubros = async (params?: GetRubrosParams) => {
  const stateParams = { ...params };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);
  return get<RubrosPagingRes>(
    `${urlBackend}api/v1/rubro/?${queryParams}`,
    true
  );
};

export const getRubro = async (id: string) => {
  try {
    return await get<Rubro>(`${urlBackend}api/v1/rubro/${id}`, true);
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createRubro = async <T>(data: CreateRubroParams<T>) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Rubro>(`${urlBackend}api/v1/rubro/`, data, true);
};

export const updateRubro = async <T>({ id, data }: UpdateRubroParams<T>) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<Rubro>(`${urlBackend}api/v1/rubro/${id}/`, data, true);
};

export const deleteRubro = async (id: string) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return remove<Rubro>(`${urlBackend}api/v1/rubro/${id}/`, true);
};

///* action types ==============================
export type CreateRubroLibreClienteData = Pick<
  Rubro,
  | 'fecha_vencimiento'
  | 'tipo_rubro'
  | 'fecha_emision'
  | 'valor_total'
  | 'valor_iva'
  | 'valor_base'
  | 'contrato'
> & {
  detalle: RubroProductDetail[];
  cliente: string;
};

export type AnularRubroData = Pick<Rubro, 'estado_rubro'>;

export type createRubroServiceData = Pick<
  Rubro,
  | 'fecha_vencimiento'
  | 'tipo_rubro'
  | 'fecha_emision'
  | 'valor_total'
  | 'valor_iva'
  | 'valor_base'
  | 'contrato'
> & {
  detalle: RubroProductDetail[];
  cliente: string;
};
